import React, { useCallback, useState, useEffect } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { motion } from 'framer-motion';
import CustomNode from './CustomNode';
import CustomEdge from './CustomEdge';

const nodeTypes = {
  custom: CustomNode,
};

const edgeTypes = {
  custom: CustomEdge,
};

const NodeEditor = ({ initialNodes = [], initialEdges = [], onNodesChange: onNodesChangeProp, onEdgesChange: onEdgesChangeProp, availableNodeTypes = [] }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Sync internal state when initialNodes/initialEdges change (for reset functionality)
  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  // Mobile touch drop handler
  useEffect(() => {
    const handleMobileDrop = (event) => {
      const { nodeType, nodeData, x, y } = event.detail;

      const newNode = {
        id: `${nodeType}-${Date.now()}`,
        type: 'custom',
        position: { x: x - 75, y: y - 40 },
        data: {
          label: nodeData.label,
          nodeType: nodeType,
          color: nodeData.color,
          icon: nodeData.icon,
        },
      };

      const newNodes = [...nodes, newNode];
      setNodes(newNodes);
      if (onNodesChangeProp) onNodesChangeProp(newNodes);

      // Auto-open sidebar
      window.dispatchEvent(
        new CustomEvent('openNodeConfig', {
          detail: { nodeType: nodeType },
        })
      );
    };

    const canvas = document.querySelector('[data-testid="node-editor"]');
    if (canvas) {
      canvas.addEventListener('mobileNodeDrop', handleMobileDrop);
      return () => canvas.removeEventListener('mobileNodeDrop', handleMobileDrop);
    }
  }, [nodes, setNodes, onNodesChangeProp]);

  const [selectedNodeType, setSelectedNodeType] = useState(null);

  const deleteEdge = useCallback(
    (edgeId) => {
      const newEdges = edges.filter((edge) => edge.id !== edgeId);
      setEdges(newEdges);
      if (onEdgesChangeProp) onEdgesChangeProp(newEdges);
    },
    [edges, setEdges, onEdgesChangeProp]
  );

  const onConnect = useCallback(
    (params) => {
      const newEdges = addEdge({
        ...params,
        type: 'custom',
        animated: true,
        style: { stroke: '#FF6B6B', strokeWidth: 2 },
        data: { onDelete: deleteEdge }
      }, edges);
      setEdges(newEdges);
      if (onEdgesChangeProp) onEdgesChangeProp(newEdges);
    },
    [edges, setEdges, onEdgesChangeProp, deleteEdge]
  );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = event.target.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');
      const nodeData = JSON.parse(event.dataTransfer.getData('nodeData'));

      if (typeof type === 'undefined' || !type) {
        return;
      }

      const position = {
        x: event.clientX - reactFlowBounds.left - 75,
        y: event.clientY - reactFlowBounds.top - 40,
      };

      const newNode = {
        id: `${type}-${Date.now()}`,
        type: 'custom',
        position,
        data: { label: nodeData.label, nodeType: type, color: nodeData.color, icon: nodeData.icon },
      };

      const newNodes = [...nodes, newNode];
      setNodes(newNodes);
      if (onNodesChangeProp) onNodesChangeProp(newNodes);

      // Auto-open sidebar to show node information
      window.dispatchEvent(new CustomEvent('openNodeConfig', {
        detail: { nodeType: type }
      }));
    },
    [nodes, setNodes, onNodesChangeProp]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleNodesChangeInternal = useCallback(
    (changes) => {
      onNodesChange(changes);
      if (onNodesChangeProp) {
        // Get updated nodes after changes
        setTimeout(() => onNodesChangeProp(nodes), 0);
      }
    },
    [onNodesChange, onNodesChangeProp, nodes]
  );

  const handleEdgesChangeInternal = useCallback(
    (changes) => {
      onEdgesChange(changes);
      if (onEdgesChangeProp) {
        setTimeout(() => onEdgesChangeProp(edges), 0);
      }
    },
    [onEdgesChange, onEdgesChangeProp, edges]
  );

  return (
    <div className="w-full h-full relative" data-testid="node-editor">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChangeInternal}
        onEdgesChange={handleEdgesChangeInternal}
        onConnect={onConnect}
        onDrop={onDrop}
        onDragOver={onDragOver}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        className="bg-background"
      >
        <Background color="#24284F" gap={16} size={1} />
        <Controls className="bg-background-paper border border-white/10 rounded-lg" />
        <MiniMap
          className="bg-background-paper border border-white/10 rounded-lg"
          nodeColor={(node) => node.data.color || '#FF6B6B'}
        />
      </ReactFlow>
    </div>
  );
};

export default NodeEditor;